import { supabase } from "../../Services/supabaseClient";

const sumAmount = (rows, field) => {
  return (rows || []).reduce((sum, row) => sum + Number(row[field] || 0), 0);
};

export const getCRMStats = async (companyId) => {
  const [
    leadsRes,
    oppsRes,
    quotesRes,
    collectionsRes,
    invoicesRes,
    activitiesRes,
    settingsRes,
  ] = await Promise.all([
    supabase
      .from("crm_leads")
      .select("id, status, lead_source, created_at")
      .eq("company_id", companyId),

    supabase
      .from("crm_opportunities")
      .select(
        `
        id,
        name,
        amount,
        probability,
        status,
        expected_close_date,
        updated_at,
        stage_id,
        crm_pipeline_stages(id, name)
      `
      )
      .eq("company_id", companyId),

    supabase
      .from("crm_quotes")
      .select("id, status, total, created_at")
      .eq("company_id", companyId),

    supabase
      .from("crm_collections")
      .select("id, status, created_at")
      .eq("company_id", companyId),

    supabase
      .from("invoices")
      .select("id, total, amount_paid, created_at")
      .eq("company_id", companyId),

    supabase
      .from("crm_activities")
      .select("id, status, due_at, related_type, created_at")
      .eq("company_id", companyId),

    supabase
      .from("crm_settings")
      .select("stale_opportunity_days")
      .eq("company_id", companyId)
      .maybeSingle(),
  ]);

  if (leadsRes.error) throw leadsRes.error;
  if (oppsRes.error) throw oppsRes.error;
  if (quotesRes.error) throw quotesRes.error;
  if (collectionsRes.error) throw collectionsRes.error;
  if (invoicesRes.error) throw invoicesRes.error;
  if (activitiesRes.error) throw activitiesRes.error;
  if (settingsRes.error) throw settingsRes.error;

  const leads = leadsRes.data || [];
  const opportunities = oppsRes.data || [];
  const quotes = quotesRes.data || [];
  const collections = collectionsRes.data || [];
  const invoices = invoicesRes.data || [];
  const activities = activitiesRes.data || [];
  const staleDays = Number(settingsRes.data?.stale_opportunity_days || 14);

  const pipelineValue = sumAmount(opportunities, "amount");
  const weighted = opportunities.reduce((sum, row) => {
    return sum + (Number(row.amount || 0) * Number(row.probability || 0)) / 100;
  }, 0);

  const convertedLeads = leads.filter(
    (row) => row.status === "converted"
  ).length;
  const totalLeads = leads.length;
  const conversionRate =
    totalLeads > 0 ? (convertedLeads / totalLeads) * 100 : 0;

  const sentQuotes = quotes.filter((row) => row.status === "sent").length;
  const acceptedQuotes = quotes.filter(
    (row) => row.status === "accepted"
  ).length;
  const quoteValue = sumAmount(quotes, "total");

  const outstandingReceivables = invoices.reduce((sum, row) => {
    return (
      sum +
      Math.max(Number(row.total_amount || 0) - Number(row.amount_paid || 0), 0)
    );
  }, 0);

  const stageSummaryMap = opportunities.reduce((map, row) => {
    const stageName = row.crm_pipeline_stages?.name || "Unassigned";
    const current = map[stageName] || { stage: stageName, count: 0, amount: 0 };
    current.count += 1;
    current.amount += Number(row.amount || 0);
    map[stageName] = current;
    return map;
  }, {});

  const stageSummary = Object.values(stageSummaryMap);

  const leadSourceMap = leads.reduce((map, row) => {
    const source = row.lead_source || "Unspecified";
    map[source] = (map[source] || 0) + 1;
    return map;
  }, {});

  const leadSourceSummary = Object.entries(leadSourceMap).map(
    ([source, count]) => ({
      source,
      count,
    })
  );

  const monthlyRevenueMap = invoices.reduce((map, row) => {
    const rawDate = row.created_at ? new Date(row.created_at) : null;
    if (!rawDate || Number.isNaN(rawDate.getTime())) return map;

    const key = `${rawDate.getFullYear()}-${String(
      rawDate.getMonth() + 1
    ).padStart(2, "0")}`;
    map[key] = (map[key] || 0) + Number(row.total_amount || 0);
    return map;
  }, {});

  const monthlyRevenue = Object.entries(monthlyRevenueMap)
    .map(([month, amount]) => ({ month, amount }))
    .sort((left, right) => left.month.localeCompare(right.month));

  const collectionSummaryMap = collections.reduce((map, row) => {
    const status = row.status || "open";
    map[status] = (map[status] || 0) + 1;
    return map;
  }, {});

  const collectionSummary = Object.entries(collectionSummaryMap).map(
    ([status, count]) => ({
      status,
      count,
    })
  );

  const now = new Date();

  const staleDeals = opportunities.filter((row) => {
    if (row.status !== "open") return false;
    if (!row.updated_at) return false;

    const updatedAt = new Date(row.updated_at);
    const diffDays =
      (now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays >= staleDays;
  });

  const overdueActivities = activities.filter((row) => {
    if (row.status !== "open") return false;
    if (!row.due_at) return false;
    return new Date(row.due_at).getTime() < now.getTime();
  });

  return {
    kpis: {
      totalLeads,
      convertedLeads,
      conversionRate,
      pipelineValue,
      weighted,
      sentQuotes,
      acceptedQuotes,
      quoteValue,
      outstandingReceivables,
      openCollections: collections.filter((row) => row.status !== "resolved")
        .length,
      staleDeals: staleDeals.length,
      overdueActivities: overdueActivities.length,
    },
    charts: {
      stageSummary,
      leadSourceSummary,
      monthlyRevenue,
      collectionSummary,
    },
  };
};
